// Read important global variables from env vars
export const API_URL: string = process.env.REACT_APP_API_URL || "";
export const SHARE_URL: string =
  process.env.NODE_ENV === "development"
    ? "http://localhost:3000"
    : process.env.REACT_APP_SHARE_URL || "";
export const SEGMENT_KEY: string = process.env.REACT_APP_SEGMENT_KEY || "";
export const POSTHOG_API_KEY: string =
  process.env.REACT_APP_POSTHOG_API_KEY || "";
export const POSTHOG_HOST: string =
  process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

// Colors
export const HOOPER_NEON_GREEN: string = "#02ff8d";
export const HOOPER_DARK_GRAY: string = "#16191a";
// Brand violet from the "H" logo tile (public/logo512.png).
export const HOOPER_PURPLE = "#5040FF";
// Hover/pressed shade for HOOPER_PURPLE.
export const HOOPER_PURPLE_DARK = "#3A2AE0";
// HOOPER_PURPLE lifted for text and accents on the dark surface, where the
// base violet is too dim to read.
export const HOOPER_PURPLE_LIGHT = "#8B80FF";
// Light tint for selected/active fills behind HOOPER_PURPLE_DARK text.
export const HOOPER_PURPLE_TINT = "#EDEBFF";

// The brand accent moved from blue to the logo's violet. The HOOPER_BLUE*
// names are kept so the ~40 call sites (and the theme's primary) follow
// without churn; they now all resolve to the purple.
export const HOOPER_BLUE = HOOPER_PURPLE;
// Hover/pressed shade for HOOPER_BLUE. Doubles as the text-on-light accent.
export const HOOPER_BLUE_DARK = HOOPER_PURPLE_DARK;
// Light tint for selected/active fills behind HOOPER_BLUE_DARK text.
export const HOOPER_BLUE_LIGHT = HOOPER_PURPLE_TINT;
// Hover/pressed shade for HOOPER_NEON_GREEN, dimmed so it reads as pressed
// against the dark surface rather than brighter.
export const HOOPER_NEON_GREEN_DARK = "#00e07c";

// Fill for the monthly-usage meters. A decorative brand gradient
// (blue -> neon green) that is deliberately fixed in both schemes, the same
// treatment the sidebar meter documented: no scheme token covers a fixed
// two-colour gradient. Shared so the sidebar meter and the Billing meter
// cannot drift apart -- they are meant to read as the same thing.
export const USAGE_METER_GRADIENT = `linear-gradient(90deg, ${HOOPER_BLUE} 0%, #0bff8d 100%)`;
export const HOOPER_RED = "#E52E4D";
export const HOOPER_YELLOW = "#F0B031";
export const HOOPER_GREEN = "#1CBE49";
// The accent's dark-surface variant (see HOOPER_BLUE above).
export const HOOPER_LIGHT_BLUE = HOOPER_PURPLE_LIGHT;
export const OFF_WHITE = "#F2F2F2";

export const GLASS_BORDER = "#3A3F45";
export const GLASS_BACKGROUND = "rgba(0, 0, 0, 0.25)";

// Bug report states
export enum BugStatus {
  OPEN = "open",
  IN_PROGRESS = "in_progress",
  RESOLVED = "resolved",
  IGNORED = "ignored",
}

// Session states
export enum SessionStatus {
  CREATED = "created",
  UPLOADING = "uploading",
  UPLOADED = "uploaded",
  WAITING = "waiting",
  PROCESSED = "processed",
  TAGGED = "tagged",
  ERROR = "error",
  VERIFIED = "verified",
}

export enum SessionMode {
  GAME = "game",
  SHOOTAROUND = "shootaround",
  PRACTICE = "practice",
}

export enum CourtType {
  HIGH_SCHOOL = "high_school",
  NCAA_WOMEN = "ncaa_women",
  NCAA_MEN = "ncaa_men",
  FIBA = "fiba",
  WNBA = "wnba",
  NBA = "nba",
}

export enum SessionType {
  SOLO = "solo",
  ONES = "1v1",
  TWOS = "2v2",
  THREES = "3v3",
  FOURS = "4v4",
  FIVES = "5v5",
}

export enum RecordingSetup {
  TRIPOD = "tripod",
  GROUND = "ground",
}

export enum CameraSetup {
  STATIONARY = "stationary",
  PANNING = "panning",
}

export enum PointSystem {
  TWOS_AND_THREES = 1,
  ONES_AND_TWOS = 2,
  ONES_ONLY = 3,
}

export enum ShotType {
  FREETHROW = "freethrow",
  FIELDGOAL = "fieldgoal",
}

// Session states
export enum TaskStatus {
  PENDING = "pending",
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
}

export enum QueueType {
  VIDEO_PROCESSING = "video-processing-queue",
  VIDEO_COMPRESSION = "video-compression-queue",
  CLIP_INFERENCE = "clip-inference-queue",
  HOOP_DETECTION = "hoop-detection-queue",
  APP_NOTIFICATION = "app-notification-queue",
}

export enum QueueJobStatus {
  WAITING = "waiting",
  RUNNING = "running",
  PROCESSED = "processed",
  ERRORED = "errored",
}

export enum UserSegmentType {
  PLAYER = "player",
  COACH = "coach",
  PARENT = "parent",
  LEAGUE = "league",
  OTHER = "other",
}

export enum SubscriptionType {
  FREE = "free",
  PRO = "pro",
  BETA = "beta",
}

// Shot outcomes
export const SHOT_STATUS_PENDING: number = 0;
export const SHOT_STATUS_MAKE: number = 1;
export const SHOT_STATUS_MISS: number = 2;

// Subscription types
export const SUBSCRIPTION_TYPE_FREE: string = "free";
export const SUBSCRIPTION_TYPE_PRO: string = "pro";

// Session statuses
export const SESSION_CREATED: string = "created";
export const SESSION_UPLOADING: string = "uploading";
export const SESSION_UPLOADED: string = "uploaded";
export const SESSION_WAITING: string = "waiting";
export const SESSION_PROCESSED: string = "processed";
export const SESSION_TAGGED: string = "tagged";
export const SESSION_ERROR: string = "error";
export const SESSION_VERIFIED: string = "verified";

// Session constants
export const GAME_MODE: string = "game";
export const PRACTICE_MODE: string = "practice";
export const SHOOTAROUND_MODE: string = "shootaround";

// Action constants
export const ACTION_SHOT = 1;
export const ACTION_REBOUND = 2;
export const ACTION_ASSIST = 3;

// Recording devices
export const TRIPOD_SETUP: string = "tripod";
export const GROUND_SETUP: string = "ground";

// Points systems
export const POINTS_TWOS_AND_THREES: number = 1;
export const POINTS_ONES_AND_TWOS: number = 2;
export const POINTS_ONES_ONLY: number = 3;

export const DEMO_USER_ID = "demo-user";

// Shot Chart region polygon coordinate
// paint left, paint right, free throw right, free throw left
export const PAINT_REGION_COORDINATES: [number, number][] = [
  [771.82, 7.989],
  [1210.29, 7.633],
  [1210.669, 655.017],
  [771.834, 654.884],
];
export const TWO_POINT_REGION_COORDINATES: [number, number][] = [
  [124.923, 7.348],
  [125.424, 51.073],
  [127.899, 88.264],
  [132.895, 125.99],
  [144.693, 190.727],
  [162.953, 257.016],
  [188.677, 331.305],
  [238.538, 434.172],
  [283.338, 505.035],
  [326.848, 561.277],
  [380.286, 619.488],
  [434.213, 668.829],
  [501.163, 719.514],
  [568.294, 761.389],
  [644.038, 799.05],
  [716.767, 827.845],
  [798.902, 850.956],
  [879.287, 866.672],
  [945.242, 872.879],
  [1003.187, 874.17],
  [1068.261, 871.318],
  [1140.304, 861.939],
  [1205.169, 850.503],
  [1293.785, 821.722],
  [1357.488, 796.648],
  [1413.578, 769.157],
  [1471.745, 734.618],
  [1534.889, 689.301],
  [1591.759, 640.853],
  [1650.418, 580.228],
  [1693.869, 527.894],
  [1724.399, 483.339],
  [1755.618, 433.11],
  [1778.251, 390.504],
  [1814.616, 306.149],
  [1838.66, 233.36],
  [1853.892, 166.583],
  [1864.2, 100.981],
  [1868.276, 55.518],
  [1868.598, 7.965],
];
export const THREE_POINT_REGION_COORDINATES: [number, number][] = [
  [9.974, 7.836],
  [10.485, 1399.652],
  [1973.859, 1400.127],
  [1973.087, 8.432],
];
export const THREE_POINT_REGION_POLYGON: [number, number][] = [
  [120, 0],
  [125, 65],
  [150, 205],
  [180, 297],
  [215, 385],
  [260, 464],
  [312, 540],
  [385, 620],
  [480, 700],
  [565, 750],
  [650, 795],
  [740, 829],
  [850, 852],
  [985, 863],
  [1090, 857],
  [1215, 834],
  [1305, 805],
  [1385, 769],
  [1460, 726],
  [1545, 665],
  [1645, 568],
  [1720, 471],
  [1765, 391],
  [1800, 316],
  [1830, 226],
  [1850, 141],
  [1855, 72],
  [1857, 0],
  [120, 0],
];
// Shot Chart zone polygon coordinates
export const BLOCK_LEFT_COORDINATES: [number, number][] = [
  [771.187, 324.627],
  [771.297, 8.188],
  [124.768, 7.472],
  [125.91, 73.116],
  [136.499, 153.617],
  [155.852, 236.994],
  [184.536, 321.954],
];
export const ELBOW_LEFT_COORDINATES: [number, number][] = [
  [184.361, 321.942],
  [771.252, 324.642],
  [773.466, 844.886],
  [714.075, 827.441],
  [635.301, 796.042],
  [547.219, 749.147],
  [443.265, 677.323],
  [352.258, 591.392],
  [277.066, 495.536],
  [210.628, 381.406],
];
export const PAINT_UPPER_RIGHT_COORDINATES: [number, number][] = [
  [988.127, 7.327],
  [1209.76, 7.603],
  [1210.513, 327.371],
  [988.863, 326.907],
];
export const PAINT_UPPER_LEFT_COORDINATES: [number, number][] = [
  [772.026, 8.137],
  [988.045, 7.417],
  [988.928, 326.925],
  [771.223, 324.546],
];
export const PAINT_BOTTOM_RIGHT_COORDINATES: [number, number][] = [
  [988.769, 327.244],
  [1210.121, 327.623],
  [1210.956, 654.415],
  [990.13, 653.952],
];
export const PAINT_BOTTOM_LEFT_COORDINATES: [number, number][] = [
  [771.032, 325.396],
  [771.648, 654.792],
  [989.667, 653.677],
  [988.652, 327.438],
];
export const KEY_ZONE_COORDINATES: [number, number][] = [
  [771.954, 655.158],
  [1210.911, 655.18],
  [1211.82, 847.001],
  [1169.857, 856.645],
  [1134.454, 862.7],
  [1058.569, 871.86],
  [991.713, 874.479],
  [914.545, 869.869],
  [836.908, 859.801],
  [773.542, 845.042],
];
export const BLOCK_RIGHT_COORDINATES: [number, number][] = [
  [1210.017, 7.604],
  [1869.772, 7.874],
  [1866.72, 80.441],
  [1856.928, 156.383],
  [1835.604, 244.238],
  [1805.562, 331.602],
  [1210.872, 327.507],
];
export const ELBOW_RIGHT_COORDINATES: [number, number][] = [
  [1805.593, 331.484],
  [1210.58, 327.531],
  [1211.718, 847.317],
  [1320.654, 811.857],
  [1413.673, 768.463],
  [1500.043, 714.832],
  [1584.233, 646.864],
  [1658.062, 570.82],
  [1717.714, 494.674],
  [1775.048, 397.535],
];
export const WING_LEFT_COORDINATES: [number, number][] = [
  [10.275, 561.462],
  [11.485, 1399.699],
  [771.615, 1399.977],
  [773.456, 845.048],
  [721.443, 829.759],
  [650.764, 802.614],
  [591.145, 774.177],
  [528.46, 738.921],
  [450.123, 682.684],
  [405.262, 643.679],
  [355.488, 594.147],
  [328.564, 563.393],
];
export const MIDDLE_THREE_POINT_COORDINATES: [number, number][] = [
  [773.44, 845.021],
  [837.304, 859.86],
  [879.157, 866.016],
  [924.214, 870.778],
  [992.063, 874.408],
  [1063.706, 871.516],
  [1119.787, 865.678],
  [1171.77, 856.73],
  [1212.118, 846.836],
  [1214.206, 1399.741],
  [771.954, 1400.083],
];
export const WING_RIGHT_COORDINATES: [number, number][] = [
  [1214.501, 1399.414],
  [1974.655, 1399.321],
  [1974.173, 562.636],
  [1665.636, 562.486],
  [1609.073, 624.881],
  [1548.215, 678.665],
  [1482.804, 726.418],
  [1424.332, 762.773],
  [1354.205, 797.954],
  [1274.079, 828.539],
  [1212.166, 846.834],
];
export const CORNER_LEFT_COORDINATES: [number, number][] = [
  [327.295, 563.291],
  [11.231, 562.225],
  [10.215, 7.799],
  [124.935, 7.845],
  [125.993, 49.249],
  [131.712, 114.09],
  [140.814, 174.289],
  [155.199, 234.712],
  [185.128, 322.911],
  [207.095, 373.854],
  [244.576, 444.762],
  [295.94, 522.952],
];
export const CORNER_RIGHT_COORDINATES: [number, number][] = [
  [1665.411, 561.975],
  [1974.733, 563.784],
  [1973.628, 8.116],
  [1869.194, 8.954],
  [1869.52, 32.163],
  [1867.708, 74.029],
  [1862.557, 120.468],
  [1850.777, 186.368],
  [1837.898, 236.854],
  [1811.99, 315.989],
  [1769.494, 407.736],
  [1712.821, 501.256],
];
export const SHOT_CHART_WIDTH = 1978;
export const SHOT_CHART_HEIGHT = 1406;
